import {noop} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'

import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as optionApi from '../../../services/api/option'
import {
  MixedFormData,
  SingleFormData,
  formDataToPartialOptionRequest,
  initialValues,
  optionsToFormData,
} from '../../forms/options/utils'
import {toMixedValidationSchema, toTouchedFormData} from '../../utils/form'

import {ModalMeta, OptionWithLinkedProviderInfos} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import TolerancesForm, {validationSchema} from '../../forms/options/tolerances/form'

type Props = {
  meta: ModalMeta
  tolerances: OptionWithLinkedProviderInfos[]
  onDone?: () => void
}

const EditTolerancesModal = ({meta, tolerances = [], onDone = noop}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const editMultiple = tolerances.length > 1

  const formik = useFormik<MixedFormData>({
    initialValues: {
      ...initialValues,
      ...optionsToFormData(tolerances),
    },
    validationSchema: toMixedValidationSchema(validationSchema, [['basePrice', 'factor']]),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>
      const toleranceRequest = formDataToPartialOptionRequest(touchedValues)

      try {
        const toleranceIds = tolerances.map(({id}) => id)
        await optionApi.patchMultiple(toleranceRequest, toleranceIds)

        onDone()
        dispatch(closeModal())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={
        <Headline
          label="Edit tolerance options"
          addendum={editMultiple && `(${tolerances.length} items)`}
          size="l"
        />
      }
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <TolerancesForm formik={formik} provider={provider} />
    </Modal>
  )
}

export default EditTolerancesModal
